<script setup lang="ts">
import {
  ref,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onServerPrefetch,
} from "vue";
import { useRoute, useRouter, RouterLink } from "vue-router";
import { storeToRefs } from "pinia";

import type { Deal, Property } from "@/types/properties";
import type {
  Agent,
  AgentWithProfiles,
  UserProfile,
  OrganizationProfile,
} from "@/types/profiles.d";

import LayoutHeader from "@/components/LayoutHeader.vue";
import LayoutBreadcrumb from "@/components/LayoutBreadcrumb.vue";
import LayoutFooter from "@/components/LayoutFooter.vue";
import IconZoom from "@/assets/icons/zoom-in.svg?component";

import { useHead } from "@unhead/vue";
import { useI18n } from "vue-i18n";
import { useContext } from "vite-ssr";
import { use as usePageLevel } from "@/stores/pageLevel";
import { useAPI } from "@/composables/api";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const context = useContext();
const page = usePageLevel();
const api = useAPI();

const { current } = storeToRefs(page);

const property = ref<Property | null>(null);
const agents = ref<AgentWithProfilesAndStats[]>([]);

interface AgentWithProfilesAndStats extends AgentWithProfiles {
  dealsCount: number;
}

const shortAddr = computed(() => {
  if (property.value) {
    const re =
      /[^\d]+((\d+|([二三四五六七八九]?十)?[一二三四五六七八九])丁目)?/;
    return property.value.address?.normalize("NFKC").match(re)?.[0];
  }
  return "";
});
const label = computed(() => {
  const { value } = property;
  if (value) {
    const station = value.nearestStations?.[0]?.name;
    // TODO i18n
    return [
      value.name ?? shortAddr.value,
      station?.match(/駅$/) ?? true ? station : station + "駅",
      value.floorPlanText ?? value.floorPlanText,
      t("buildingType." + value.buildingType),
    ]
      .filter((v) => v)
      .join(" ");
  }
  return "";
});
const description = computed(() => {
  const { value } = property;
  if (value) {
    const arr = [
      [
        shortAddr.value + "の",
        value.floorPlanText,
        t("buildingType." + value.buildingType),
        value.name && `、${value.name}`,
        value.roomNumber,
        "の物件詳細情報です",
      ]
        .filter((v) => v)
        .join(""),
    ];
    if (value.nearestStations) {
      arr.push(
        ...value.nearestStations
          .map((s) => [s.line, s.name, "まで徒歩", s.minutes, "分"].join(""))
          .filter((v) => v)
      );
    }
    if (value.catchphrases) {
      arr.push(...value.catchphrases);
    }
    return arr.concat("").join("。");
  }
  return label.value + "の物件詳細情報です。";
});
const title = computed(() => (label.value ? label.value + "の物件詳細" : ""));
const canonical = computed(() => {
  const origin = import.meta.env.VITE_SEO_ORIGIN;
  if (page.path) {
    return origin + "/" + page.path + "/detail/" + route.params.id;
  } else if (property.value) {
    const type =
      property.value.buildingType == "income"
        ? "invest"
        : property.value.type == "sale"
        ? "buy"
        : "rent";
    const buildingType =
      property.value.buildingType == "income"
        ? "condo"
        : property.value.buildingType;
    return [origin, type, buildingType, "detail", route.params.id].join("/");
  }
  return [origin, "detail", route.params.id].join("/");
});

useHead({
  title: () => title.value,
  meta: [
    { name: "description", content: () => description.value },
    { name: "og:title", content: () => title.value },
    { name: "og:description", content: () => description.value },
    { name: "og:image", content: () => property.value?.images.thumbnail.url },
    { name: "og:url", content: () => canonical.value },
  ],
  link: [{ rel: "canonical", href: () => canonical.value }],
});

interface AgentResponse {
  agent: UserProfile;
  company: OrganizationProfile;
  properties: { dealsCount: number };
}
async function load() {
  const {
    data: { page, ...data },
  } = await api.call("property", route.params.id);

  if ((page && !route.params.levels) || route.params.levels == "") {
    const newPath = "/" + page.path + "/detail/" + route.params.id;
    if (import.meta.env.SSR) {
      return context.redirect(newPath);
    } else {
      await router.replace("/" + page.path + "/detail/" + route.params.id);
    }
  }

  const deal = data.deals?.find((d: Deal) => d.images);
  if (deal) Object.assign(data, deal);

  property.value = data;
  current.value = {
    title: title.value,
    name: title.value,
    slug: "/detail/" + route.params.id,
    path: route.path,
  };

  agents.value = await Promise.all(
    data.agents.map(async (agent: Agent): Promise<AgentWithProfiles> => {
      const { id, organizationId } = agent;
      const { data: groups } = await api.call<AgentResponse>(
        "agent",
        id,
        organizationId
      );
      return {
        ...agent,
        ...groups.properties,
        profile: groups.agent,
        company: groups.company,
      };
    })
  );
}

function getAvatar(agent: AgentWithProfiles): string {
  if (agent.profile?.avatar.url) {
    return agent.profile.avatar.url;
  } else if (agent.profile?.avatar.data) {
    return "data:image/png;base64," + agent.profile.avatar.data;
  } else {
    return "/images/avatar.svg";
  }
}

function getAddress(agent: AgentWithProfiles): string {
  const company = agent.company.general;
  return [company?.prefecture, company?.city, company?.street]
    .filter((v) => v)
    .join("");
}

const numberFormat = new Intl.NumberFormat();
function formatNumber(n: number) {
  return numberFormat.format(n);
}

onServerPrefetch(async () => {
  await load();
  context.initialState.property = property.value;
  context.initialState.agents = agents.value;
});
onBeforeMount(async () => {
  property.value = context.initialState.property ?? null;
  agents.value = context.initialState.agents ?? [];
  if (!property.value || !agents.value.length) await load();
});
onBeforeUnmount(() => {
  delete context.initialState.property;
  delete context.initialState.agents;
});

// maps
import { GoogleMap } from "vue3-google-map";
import MarkerProperty from "@/components/markers/Property.vue";
import { geoPointToLatLng } from "@/utils/geo";
const key = import.meta.env.VITE_GM_API_KEY;
</script>
<template>
  <LayoutHeader />
  <LayoutBreadcrumb />
  <main class="frame">
    <pre v-if="false">{{ JSON.stringify(property, null, 2) }}</pre>
    <h1 class="w6">{{ title }}</h1>
    <div class="property u-row" v-if="property && agents.length">
      <div class="info u-col">
        <div
          class="image"
          @click="$popup.show('zoom', { url: property.images.large.url })"
        >
          <img
            class="u-flex"
            :src="property.images.large.url"
            :alt="t('image.alt')"
            :title="t('image.alt')"
          />
          <button
            class="zoom u-row"
            type="button"
            @click.stop="
              $popup.show('zoom', { url: property.images.large.url })
            "
          >
            <IconZoom />
            <span v-t="'zoom'" />
          </button>
        </div>
        <div class="tags u-row">
          <h3 class="tag u-trunc w6" v-for="tag of property.labels" :key="tag">
            {{ tag }}
          </h3>
        </div>
        <div class="divider" />
        <h2 class="w6" v-t="'location'" />
        <div class="address u-row">
          <FontAwesomeIcon class="cl-blue" :icon="['fas', 'map-marker-alt']" />
          <h3 class="value u-trunc u-flex">{{ property.address }}</h3>
          <a
            target="_blank"
            class="cl-blue"
            :href="
              'https://www.google.com/maps/search/?api=1&query=' +
              encodeURIComponent(property.address)
            "
            v-if="property.address"
            v-t="'viewOnMap'"
          />
        </div>
        <ClientOnly>
          <GoogleMap
            ref="mapRef"
            class="map"
            :api-key="key"
            :center="geoPointToLatLng(property.location)"
            :zoom="16"
            :full-screen-control="false"
            :map-type-control="false"
          >
            <MarkerProperty
              class="marker"
              :options="{
                position: geoPointToLatLng(property.location),
              }"
            />
          </GoogleMap>
        </ClientOnly>
      </div>
      <div class="divider" />
      <div class="agents u-flex">
        <h2 class="title h4">この物件を取り扱うエージェント</h2>
        <div class="agent u-col" v-for="agent of agents" :key="agent.id">
          <RouterLink
            class="head u-row"
            :href="'/agents/' + agent.organizationId + '/' + agent.id"
            :to="{
              name: 'agentHome',
              params: { id: agent.id, organizationId: agent.organizationId },
            }"
          >
            <div
              class="avatar"
              :style="{ backgroundImage: 'url(' + getAvatar(agent) + ')' }"
            />
            <div class="right u-col">
              <h4 class="fullname t16 w6">
                {{ agent.profile.personal?.fullname }}
              </h4>
              <p>
                取扱物件数
                <span class="cl-blue t16 w6">{{
                  formatNumber(agent.dealsCount)
                }}</span>
              </p>
            </div>
          </RouterLink>
          <div class="agent-info u-col">
            <div class="row u-row" v-if="agent.company.general?.name">
              <div class="icon">
                <FontAwesomeIcon :icon="['fas', 'building']" />
              </div>
              <span class="u-trunc">{{ agent.company.general?.name }}</span>
            </div>
            <div class="row u-row" v-if="agent.company.general?.prefecture">
              <div class="icon">
                <FontAwesomeIcon :icon="['fas', 'map-marker-alt']" />
              </div>
              <span class="u-trunc">{{ getAddress(agent) }}</span>
            </div>
            <div class="row u-row" v-if="agent.company.general?.phone">
              <div class="icon">
                <FontAwesomeIcon :icon="['fas', 'phone']" />
              </div>
              <a class="u-trunc" :href="'tel:' + agent.company.general?.phone">
                {{ agent.company.general?.phone }}
              </a>
            </div>
            <div class="row u-row" v-if="agent.company.general?.website">
              <div class="icon">
                <FontAwesomeIcon :icon="['fas', 'link']" />
              </div>
              <a class="u-trunc" :href="agent.company.general?.website">
                {{ agent.company.general?.website }}
              </a>
            </div>
          </div>
          <button
            type="button"
            class="btn primary"
            @click="
              $popup.show('inquiry', {
                app: 'openroom',
                agent,
                documentId: property.meta.documentId,
              })
            "
            v-if="property.meta?.documentId"
          >
            <span v-t="'contact'" />
          </button>
        </div>
      </div>
    </div>
  </main>
  <LayoutFooter />
</template>
<style scoped>
main {
  padding-bottom: 40px;
}
h1 {
  font-size: 20px;
  margin-top: 30px;
  margin-bottom: 32px;
}
.u-row,
.u-col {
  align-items: unset;
}
.close {
  align-self: end;
}
.property {
  width: 100%;
  margin-top: 24px;
}
.property .info {
  width: 560px;
  min-width: 50%;
  margin-right: 48px;
}
.property .info .tags {
  flex-wrap: wrap;
}
.property .info .tags .tag {
  border-radius: 3px;
  border: 1px solid var(--base-black-20, #ced1cf);
  background: var(--base-black-00, #fff);
  padding: 0 8px;
  height: 28px;
  line-height: 28px;
  font-size: 12px;
  margin-right: 8px;
  margin-top: 8px;
  flex-shrink: 0;
}
.property .info h2 {
  margin-bottom: 16px;
  font-size: 16px;
}
.property .info .address svg {
  height: 19px;
  margin-right: 8px;
}
.property .info .address a {
  flex-shrink: 0;
}
.property .info .map {
  margin-top: 16px;
  height: 400px;
}
.property .image {
  position: relative;
  cursor: zoom-in;
}
.property .image img {
  width: 100%;
  border-radius: 10px;
  border: solid 1px #ced1cf;
  margin-bottom: 24px;
}
.property .image .zoom {
  padding: 0 16px;
  align-items: center;
  position: absolute;
  height: 40px;
  border-radius: 20px;
  left: 50%;
  bottom: 22px;
  background: var(--cl-black);
  color: var(--cl-white);
  opacity: 0.6;
  transform: translateX(-50%);
}
.property .image .zoom svg {
  height: 20px;
  width: 20px;
  margin-right: 4px;
}
.property .divider {
  border-top: solid 1px var(--cl-black-10);
  margin: 24px 0;
}
.property .divider.mobile {
  display: none;
}
.agents {
  overflow-y: auto;
  padding: 0 16px 16px;
}
.agents .title {
  margin-bottom: 24px;
}
.agents .agent {
  width: 100%;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
}
.agents .agent + .agent {
  margin-top: 16px;
}
.agents .agent .head .avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin-right: 8px;
}
.agents .agent .head {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: solid 1px #e6e8e7;
}
.agents .agent .head .fullname {
  margin-top: 0;
  margin-bottom: 8px;
}
.agents .agent .agent-info:not(:last-child) {
  margin-bottom: 16px;
}
.agents .agent .agent-info .row {
  align-items: center;
  color: #6b7470;
  height: 20px;
}
.agents .agent .agent-info .row + .row {
  margin-top: 8px;
}
.agents .agent .agent-info .row .icon {
  margin-right: 8px;
  width: 20px;
  height: 20px;
}
.agents .agent .agent-info .row .icon svg {
  display: block;
  margin: auto;
  width: 14px;
  height: 18px;
}
.agents .agent .agent-info .row svg {
  fill: #6b7470;
}
.agents .agent .agent-info .row svg circle {
  fill: var(--cl-white);
}
.agents .agent button {
  font-size: 15px;
  font-weight: 600;
  padding: 0 16px;
  background: #f39800;
  box-shadow: 0px 2px 10px 2px rgba(46, 212, 122, 0.1);
}
.view {
  background-position: center;
}
@media (max-width: 1060px) {
  .property {
    flex-direction: column;
  }
  .property > * {
    width: 100%;
  }
  .property .info {
    width: 100%;
    margin: 0;
  }
  .property .divider {
    display: block;
  }
  .agents {
    margin: 0;
  }
  .agents .agent {
    width: 100%;
  }
}
</style>
<style>
body {
  overflow-y: auto;
}
h2,
h3,
h4 {
  font-size: unset;
  font-weight: unset;
  font-style: unset;
}
</style>

<i18n lang="yaml">
en:
  location: "Location"
  viewOnMap: "View on map"
  contact: "Contact this agent"
  image:
    alt: "Property onepager"
ja:
  location: "所在地"
  viewOnMap: "マップで見る"
  contact: "この担当者に問い合わせる"
  image:
    alt: "物件チラシ"
</i18n>
